/**
 * Recovery for stale Firebase IndexedDB databases.
 *
 * Symptom: `[FCM] Failed to obtain FCM token: VersionError: The requested
 * version (1) is less than the existing version (2)`.
 *
 * Why it happens: IndexedDB is per-origin and shared between the page and the
 * service worker. An older Firebase SDK on this origin left these databases at a
 * higher version than `firebase@12` opens them with. IndexedDB forbids opening
 * below the existing version and the SDK hardcodes its version, so every
 * `getToken()` fails for the life of that browser profile.
 *
 * Recovery: delete the databases. They hold only a cached FCM token, the Firebase
 * installation id and heartbeat telemetry — all re-created on the next
 * `getToken()`, at the cost of one token re-registration.
 */

import { log } from '@common/core-layer/services/logger.service'

/** The database whose stale version actually breaks `getToken()`. */
export const FIREBASE_MESSAGING_DB_NAME = 'firebase-messaging-database'

/** Firebase-owned databases and the version the current SDK opens them with. */
export const FIREBASE_IDB_DATABASES: ReadonlyArray<{ name: string; expectedVersion: number }> = [
  { name: FIREBASE_MESSAGING_DB_NAME, expectedVersion: 1 },
  { name: 'firebase-installations-database', expectedVersion: 1 },
  { name: 'firebase-heartbeat-database', expectedVersion: 1 },
  // Pre-v9 store. The SDK migrates and drops it itself, but a stale version here fails the same way.
  { name: 'fcm_token_details_db', expectedVersion: 5 },
]

/** sessionStorage key guarding the one-shot reload after a recovery purge. */
export const FIREBASE_IDB_RECOVERY_FLAG = 'fcm_idb_recovery_attempted'

/** True for the IndexedDB "requested version is less than the existing version" failure. */
export function isIndexedDbVersionError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false

  const { name, message } = err as { name?: unknown; message?: unknown }
  if (name === 'VersionError') return true

  // Firebase wraps the DOMException, so the original name survives only in the message.
  const text = typeof message === 'string' ? message : ''
  return text.includes('VersionError') || text.includes('less than the existing version')
}

/** Deletes one database. Never rejects — an error or a blocked delete resolves as `false`. */
function deleteIndexedDb(factory: IDBFactory, name: string): Promise<boolean> {
  return new Promise(resolve => {
    try {
      const request = factory.deleteDatabase(name)
      request.onsuccess = () => resolve(true)
      request.onerror = () => resolve(false)
      // Another tab holds the connection open; the delete lands when that tab closes.
      request.onblocked = () => resolve(false)
    } catch (err) {
      log.warn('[FCM] Failed to delete IndexedDB database', { labels: { database: name }, error: err instanceof Error ? err : undefined })
      resolve(false)
    }
  })
}

/**
 * Names of Firebase databases that exist at a higher version than the current SDK
 * requests.
 *
 * `indexedDB.databases()` is unavailable in Firefox — callers must treat an empty
 * result as "unknown", not as "nothing stale".
 */
export async function findStaleFirebaseIndexedDbs(factory: IDBFactory): Promise<string[]> {
  if (typeof factory.databases !== 'function') return []

  try {
    const existing = await factory.databases()
    return FIREBASE_IDB_DATABASES.filter(({ name, expectedVersion }) =>
      existing.some(db => db.name === name && typeof db.version === 'number' && db.version > expectedVersion)
    ).map(({ name }) => name)
  } catch (err) {
    log.warn('[FCM] Failed to enumerate IndexedDB databases', { error: err instanceof Error ? err : undefined })
    return []
  }
}

/**
 * Deletes the named Firebase databases — every one of them when `names` is
 * omitted, which is the last-resort recovery for browsers that cannot enumerate.
 * Returns the names actually removed.
 */
export async function deleteFirebaseIndexedDbs(factory: IDBFactory, names?: readonly string[]): Promise<string[]> {
  const targets = names ?? FIREBASE_IDB_DATABASES.map(({ name }) => name)
  if (!targets.length) return []

  const results = await Promise.all(targets.map(async name => ((await deleteIndexedDb(factory, name)) ? name : null)))
  return results.filter((name): name is string => Boolean(name))
}

/** Whether a recovery reload has already been spent for this browsing session. */
export type FirebaseIdbRecoveryGuard = 'attempted' | 'pending' | 'unavailable'

/** Reads the one-shot recovery guard. `unavailable` means storage is blocked, not unset. */
export function readFirebaseIdbRecoveryGuard(storage: Storage | undefined): FirebaseIdbRecoveryGuard {
  try {
    if (!storage) return 'unavailable'
    return storage.getItem(FIREBASE_IDB_RECOVERY_FLAG) ? 'attempted' : 'pending'
  } catch {
    return 'unavailable'
  }
}

/** Marks the guard as spent. Returns false when storage refused the write. */
export function persistFirebaseIdbRecoveryAttempt(storage: Storage | undefined): boolean {
  try {
    if (!storage) return false
    storage.setItem(FIREBASE_IDB_RECOVERY_FLAG, '1')
    return true
  } catch {
    return false
  }
}
